import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ArrowRight, Check, HelpCircle, Minus, Zap } from 'lucide-react'
import { useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { BlurText, SpotlightCard } from '@/components/reactbits'
import { createCheckoutSession, PlatformApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

const ease = [0.22, 1, 0.36, 1] as const

interface Tier {
  key: string
  name: string
  price: string
  unit: string
  description: string
  cta: string
  featured: boolean
  features: string[]
  highlight?: string
}

const tiers: Tier[] = [
  {
    key: 'starter',
    name: 'Starter',
    price: '€29',
    unit: '/mo',
    description: 'Everything you need to get your team on a modern ERP.',
    cta: 'Start free trial',
    featured: false,
    features: [
      'Up to 10 users',
      '1 workspace',
      'All built-in modules (HR, Finance, Inventory, CRM, Projects)',
      'Custom fields & layouts',
      'Email support',
      'Community access',
    ],
  },
  {
    key: 'business',
    name: 'Business',
    price: '€79',
    unit: '/mo',
    description: 'For growing companies that need full customization power.',
    cta: 'Start free trial',
    featured: true,
    highlight: 'Most popular',
    features: [
      'Up to 50 users',
      'Unlimited workspaces',
      'All built-in modules',
      'Custom modules & entities',
      'Workflow automation engine',
      'Webhook integrations',
      'Full audit log',
      'API access',
      'Priority support',
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    unit: '',
    description: 'For organizations with compliance and infrastructure needs.',
    cta: 'Talk to sales',
    featured: false,
    features: [
      'Unlimited users & workspaces',
      'Everything in Business',
      'Dedicated infrastructure',
      'SSO / SAML integration',
      'SLA guarantee (99.9%+)',
      'Hands-on onboarding',
      'Custom integrations',
      'Invoice billing',
    ],
  },
]

const comparisonCategories = [
  {
    name: 'Platform',
    features: [
      { name: 'Users', starter: '10', business: '50', enterprise: 'Unlimited' },
      { name: 'Workspaces', starter: '1', business: 'Unlimited', enterprise: 'Unlimited' },
      { name: 'Storage', starter: '5 GB', business: '50 GB', enterprise: 'Unlimited' },
    ],
  },
  {
    name: 'Modules',
    features: [
      { name: 'Built-in modules (HR, Finance, etc.)', starter: true, business: true, enterprise: true },
      { name: 'Custom modules', starter: false, business: true, enterprise: true },
      { name: 'Custom entities & fields', starter: true, business: true, enterprise: true },
      { name: 'Layout editor', starter: true, business: true, enterprise: true },
    ],
  },
  {
    name: 'Automation',
    features: [
      { name: 'Workflow engine', starter: false, business: true, enterprise: true },
      { name: 'Webhook integrations', starter: false, business: true, enterprise: true },
      { name: 'Calculation engine', starter: false, business: true, enterprise: true },
      { name: 'Scheduled workflows (cron)', starter: false, business: true, enterprise: true },
    ],
  },
  {
    name: 'Security & Compliance',
    features: [
      { name: 'RBAC (role-based access)', starter: true, business: true, enterprise: true },
      { name: 'Field-level permissions', starter: false, business: true, enterprise: true },
      { name: 'Full audit log', starter: false, business: true, enterprise: true },
      { name: 'SSO / SAML', starter: false, business: false, enterprise: true },
      { name: 'Dedicated infrastructure', starter: false, business: false, enterprise: true },
      { name: 'SLA guarantee', starter: false, business: false, enterprise: true },
    ],
  },
  {
    name: 'Support',
    features: [
      { name: 'Community access', starter: true, business: true, enterprise: true },
      { name: 'Email support', starter: true, business: true, enterprise: true },
      { name: 'Priority support', starter: false, business: true, enterprise: true },
      { name: 'Dedicated account manager', starter: false, business: false, enterprise: true },
      { name: 'Onboarding assistance', starter: false, business: false, enterprise: true },
    ],
  },
]

const faqs = [
  {
    q: 'Is there a free trial?',
    a: 'Yes — every plan starts with a 14-day free trial. No credit card required. You can upgrade, downgrade, or cancel at any time.',
  },
  {
    q: 'What happens when I hit my user limit?',
    a: "You'll be prompted to upgrade. Existing users continue working — we never lock you out of your own data.",
  },
  {
    q: 'Can I switch plans later?',
    a: 'Absolutely. Upgrade or downgrade from your account settings at any time. Changes take effect on your next billing cycle.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes — annual plans come with 2 months free. Contact us or switch to annual billing from your account settings.',
  },
  {
    q: 'Where is my data stored?',
    a: 'Each workspace gets its own isolated Postgres database. Business and Starter plans run on shared infrastructure in the EU. Enterprise gets dedicated infrastructure in your preferred region.',
  },
  {
    q: 'Can I self-host instead?',
    a: 'Yes. Beyond is open-source. You can run it on your own infrastructure at no cost. The cloud plans are for teams who want managed hosting, backups, and support.',
  },
]

function PricingPage() {
  const [loadingTier, setLoadingTier] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  async function handleSelectTier(tier: Tier) {
    if (tier.key === 'enterprise') {
      window.location.href = 'mailto:sales@beyond.cloud?subject=Enterprise%20inquiry'
      return
    }
    setLoadingTier(tier.key)
    setError(null)
    try {
      const { url } = await createCheckoutSession(tier.key, `${tier.name} Workspace`)
      if (url) window.location.href = url
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.status === 401) {
          window.location.href = '/sign-up'
          return
        }
        setError(err.message)
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setLoadingTier(null)
    }
  }

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        {/* ---- Hero ---- */}
        <section className="relative overflow-hidden bg-[#08080c] pt-36 pb-28">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.1]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }}
          />
          <div
            className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-[500px] w-[800px] rounded-full opacity-[0.08] blur-[120px]"
            style={{ background: '#5865F2' }}
          />

          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-white/50">
                <Zap className="h-3 w-3" />
                14-day free trial on every plan
              </span>
            </motion.div>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              <BlurText
                text="Pricing that scales"
                delay={50}
                animateBy="words"
                stepDuration={0.3}
                className="block"
              />
              <span className="block mt-1">
                <BlurText text="with " delay={50} animateBy="words" stepDuration={0.3} />
                <BlurText
                  text="your ambition"
                  delay={50}
                  animateBy="words"
                  stepDuration={0.3}
                  className="text-primary font-extrabold"
                />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4, ease }}
              className="mx-auto mt-5 max-w-lg text-base text-white/35"
            >
              Start free, pay when you're ready. Every plan includes all built-in modules.
              No per-seat fees — one price per workspace.
            </motion.p>
          </div>
        </section>

        {/* ---- Pricing cards ---- */}
        <section className="relative -mt-10 pb-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {error && (
              <div className="mx-auto mb-6 max-w-lg rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-center text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="grid gap-5 lg:grid-cols-3 lg:items-start">
              {tiers.map((tier, index) => (
                <motion.div
                  key={tier.key}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.1 * index, ease }}
                >
                  <SpotlightCard
                    spotlightColor={
                      tier.featured
                        ? 'rgba(88, 101, 242, 0.08)'
                        : 'rgba(255, 255, 255, 0.04)'
                    }
                    spotlightSize={350}
                    className={cn(
                      'rounded-2xl transition-all',
                      tier.featured
                        ? 'border-2 border-primary/60 bg-card shadow-xl shadow-primary/[0.06] lg:scale-[1.04]'
                        : 'border border-border bg-card',
                    )}
                  >
                    <div className="p-7 sm:p-8">
                      {tier.highlight && (
                        <div className="mb-5 -mt-1">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                            <Zap className="h-3 w-3" />
                            {tier.highlight}
                          </span>
                        </div>
                      )}

                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        {tier.name}
                      </h3>

                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="text-4xl font-bold tracking-tight">{tier.price}</span>
                        {tier.unit && (
                          <span className="text-sm text-muted-foreground">{tier.unit}</span>
                        )}
                      </div>

                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                        {tier.description}
                      </p>

                      <button
                        type="button"
                        disabled={loadingTier !== null}
                        onClick={() => handleSelectTier(tier)}
                        className={cn(
                          'mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-all duration-200',
                          tier.featured
                            ? 'bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(88,101,242,0.3)] hover:opacity-95'
                            : 'border border-border bg-transparent hover:bg-muted/60',
                          loadingTier === tier.key && 'opacity-60 cursor-wait',
                        )}
                      >
                        {loadingTier === tier.key ? 'Redirecting...' : tier.cta}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>

                      <ul className="mt-7 space-y-2.5 border-t border-border/60 pt-6">
                        {tier.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2.5 text-[13px]">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/80" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </SpotlightCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- Comparison table ---- */}
        <section className="py-24 bg-[#08080c]">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, ease }}
              className="text-center"
            >
              <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Compare every detail
              </h2>
              <p className="mt-2 text-sm text-white/30">
                Not sure which plan fits? Here's the full breakdown.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
              className="mt-12 overflow-x-auto"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="pb-4 text-left font-medium text-white/30 w-[40%]" />
                    {tiers.map((tier) => (
                      <th
                        key={tier.key}
                        className={cn(
                          'pb-4 text-center font-semibold w-[20%]',
                          tier.featured ? 'text-primary' : 'text-white/60',
                        )}
                      >
                        {tier.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonCategories.map((category) => (
                    <>
                      <tr key={`cat-${category.name}`}>
                        <td
                          colSpan={4}
                          className="pt-6 pb-2 text-xs font-semibold uppercase tracking-wider text-white/20"
                        >
                          {category.name}
                        </td>
                      </tr>
                      {category.features.map((feature) => (
                        <tr
                          key={feature.name}
                          className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="py-3 text-white/40">{feature.name}</td>
                          {(['starter', 'business', 'enterprise'] as const).map((tierKey) => {
                            const value = feature[tierKey]
                            return (
                              <td key={tierKey} className="py-3 text-center">
                                {typeof value === 'boolean' ? (
                                  value ? (
                                    <Check className="mx-auto h-4 w-4 text-emerald-400/70" />
                                  ) : (
                                    <Minus className="mx-auto h-4 w-4 text-white/10" />
                                  )
                                ) : (
                                  <span className="font-medium text-white/60">{value}</span>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <section className="py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, ease }}
              className="text-center"
            >
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Frequently asked questions
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Can't find what you're looking for?{' '}
                <a
                  href="mailto:support@beyond.cloud"
                  className="text-primary hover:underline"
                >
                  Reach out
                </a>{' '}
                and we'll help.
              </p>
            </motion.div>

            <div className="mt-10 space-y-1">
              {faqs.map((faq, i) => (
                <motion.div
                  key={faq.q}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: 0.03 * i, ease }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full items-start gap-3 rounded-xl px-5 py-4 text-left transition-colors hover:bg-muted/40"
                  >
                    <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{faq.q}</div>
                      {openFaq === i && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.25, ease }}
                          className="mt-2 text-sm text-muted-foreground leading-relaxed"
                        >
                          {faq.a}
                        </motion.p>
                      )}
                    </div>
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- Final CTA ---- */}
        <section className="relative overflow-hidden bg-[#08080c] py-20">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }}
          />
          <div
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[500px] rounded-full opacity-[0.1] blur-[100px]"
            style={{ background: '#5865F2' }}
          />
          <div className="relative mx-auto max-w-2xl px-4 text-center sm:px-6">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Ready to modernize your operations?
            </h2>
            <p className="mt-3 text-sm text-white/35">
              Start your 14-day free trial today. No credit card, no commitment.
            </p>
            <div className="mt-7 flex justify-center gap-4">
              <Link
                to="/sign-up"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#08080c] transition-all duration-300 hover:shadow-[0_0_28px_rgba(88,101,242,0.25)]"
              >
                Get started free
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <a
                href="mailto:sales@beyond.cloud"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 px-7 py-3 text-sm font-medium text-white/50 transition-colors hover:text-white/80 hover:border-white/20"
              >
                Talk to sales
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
